import { tz, TZDate } from '@date-fns/tz'
import { faClock } from '@fortawesome/free-solid-svg-icons'
import { formatDate, formatTimezone } from '@unimpaired/utils'
import * as chrono from 'chrono-node'
import {
  format,
  getHours,
  getMinutes,
  isDate,
  isEqual,
  isValid,
  set,
  startOfDay,
} from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { Menu } from '../../../component-exports'
import { useIsMobile } from '../../../../hooks'
import { Input, InputProps } from '../../Input'
import { TimePicker } from './TimePicker'
import { OpenMenuButton } from './components'
import { userTimezone } from './helpers'

export type TimeSelectProps = Omit<
  InputProps,
  'defaultValue' | 'label' | 'name' | 'form'
> & {
  label?: string
  name?: string
  timezone?: string
  date: Date | string | undefined
  setDate: (value: Date) => void
  disableDates?: (date: Date) => boolean
}

const timeParser = chrono.casual.clone()
timeParser.parsers.push({
  pattern: () => {
    // match 1-4 digits, and a/p, returning hour/minute groups
    return /^(?:(\d{1,2})(\d{2})|(\d{1,2}))(?:([ap])m?)?$/i
  },
  extract: (context, match) => {
    const hour = parseInt(match[1] ?? match[3]) ?? 0
    const minute = parseInt(match[2] ?? 0) ?? 0
    const minuteText = String(minute).padStart(2, '0')
    const meridiem = match[4] === 'a' ? 0 : match[4] === 'p' ? 1 : undefined
    const meridiemText = meridiem !== undefined ? match[4] + 'm' : ''
    return context.createParsingResult(
      0,
      `${hour}:${minuteText} ${meridiemText}`,
      {
        // This should work but it doesn't
        // meridiem <--
        // so instead we just add 12 hours if meridiem (e.g. if pm)
        hour: meridiem ? hour + 12 : hour,
        minute,
      },
    )
  },
})

export const TimeSelect = ({
  name = 'time',
  date: naturalDate,
  setDate: setParentDate,
  timezone,
  ...props
}: TimeSelectProps) => {
  const options = { in: tz(timezone ?? userTimezone) }
  const newDate = new TZDate(new Date(), timezone ?? userTimezone)
  const date = !naturalDate
    ? newDate
    : isDate(naturalDate)
    ? new TZDate(naturalDate, timezone ?? userTimezone)
    : timeParser.parseDate(naturalDate, {
        instant: new Date(newDate.getTime()),
        timezone: formatTimezone(timezone ?? userTimezone, 'short'),
      }) ?? newDate

  const parseNaturalLanguageDate = (datestring: string) =>
    new TZDate(
      timeParser.parseDate(
        datestring,
        {
          // necessary to pass a native date for this to work
          // TZDates break the parser
          // instant: new Date(startOfDay(date, options).getTime()),
          instant: new Date(startOfDay(date, options).getTime()),
          timezone: formatTimezone(timezone ?? userTimezone, 'short'),
        },
        { forwardDate: true },
      ) ?? date,
      timezone ?? userTimezone,
    )

  const setDate = (newDate: Date | '') => {
    if (!newDate || !isDate(newDate)) setParentDate(date)

    setParentDate(
      set(date, {
        hours: getHours(newDate, options),
        minutes: getMinutes(newDate, options),
      }),
    )
  }

  if (!isDate(naturalDate)) setDate(date)

  const setNaturalLanguageDate = (newDate: string) => {
    const parsedDate = parseNaturalLanguageDate(newDate)
    // if the date is invalid or disallowed
    // reset the form date to the valid date
    // so it displays correctly
    if (props.disableDates?.(parsedDate)) {
      mockForm.setValue(name, date, {
        shouldValidate: true,
      })
      return
    }
    setDate(parsedDate)
    mockForm.setValue(name, parsedDate, {
      shouldValidate: true,
    })
  }

  const [menuOpen, setMenuOpen] = useState(false)
  const isMobile = useIsMobile()
  const ref = useRef<HTMLButtonElement>(null)
  const mockForm = useForm({
    defaultValues: {
      [name]: date,
    },
    mode: 'onChange',
  })

  // update the forms date when the outside date prop changes
  useEffect(() => {
    mockForm.setValue(
      name,
      !naturalDate
        ? date
        : isDate(naturalDate)
        ? naturalDate
        : parseNaturalLanguageDate(naturalDate),
    )
  }, [naturalDate])

  return (
    <>
      <Menu
        className='flex items-stretch gap-4 min-w-72'
        triggerRef={ref}
        onClose={() => setMenuOpen(false)}
        isOpen={menuOpen}>
        <TimePicker
          className='grow'
          date={date}
          disableDates={props.disableDates}
          setDate={(date) => {
            setDate(date)
            setMenuOpen(false)
          }}
          timezone={timezone ?? userTimezone}
        />
      </Menu>
      {/* @ts-expect-error */}
      <Input
        {...props}
        form={mockForm}
        label={props.label ?? 'Time'}
        name={name}
        // On mobile we set type to time to use the native pickers
        type={isMobile ? 'time' : 'text'}
        format={(e) =>
          !isDate(e)
            ? e
            : !isMobile
            ? // on desktop we can use a nice long readable format
              formatDate('time', e, timezone ?? userTimezone)
            : // on mobile set the format of the time to match the format of the html time input
              format(e, 'HH:mm', { in: tz(timezone ?? userTimezone) })
        }
        // on desktop we use our own pickers, so we show the button
        // the native button won't be there since it is a text type input
        appendElement={
          !isMobile && (
            <OpenMenuButton
              ref={ref}
              icon={faClock}
              onClick={() => setMenuOpen((open) => !open)}
            />
          )
        }
        inputClasses={twMerge('bg-base-0', isMobile && 'pr-1')}
        validate={{
          'Enter a valid time': (e) =>
            isValid(new Date(e)) || isValid(timeParser.parseDate(e)),
        }}
        // We fire change events on mobile to capture the result of setting
        // the input content via native pickers
        // but on desktop, we only do this on blur to prevent a bunch of parsing
        // of potentially incomplete natural language dates
        onChange={isMobile ? setNaturalLanguageDate : undefined}
        onBlur={setNaturalLanguageDate}
      />
    </>
  )
}
